import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { event } from '../content/event';
import { submitRsvp } from '../services/rsvpApi';
import { trackEvent } from '../services/analytics';
import FormField from './FormField';

const initialValues = {
  attendanceStatus: '', name: '', email: '', partySize: '', dish: '',
  dietaryAccessibility: '', message: '', website: '',
};
const fieldOrder = ['attendanceStatus', 'name', 'email', 'partySize', 'dish', 'dietaryAccessibility', 'message'];

export function validateRsvp(values) {
  const errors = {};
  const name = values.name.trim();
  const email = values.email.trim();
  if (!values.attendanceStatus) errors.attendanceStatus = 'Please choose yes or no.';
  if (name.length < 2) errors.name = 'Please enter at least 2 characters.';
  else if (name.length > 100) errors.name = 'Name must be 100 characters or fewer.';
  if (!email) errors.email = 'Please enter your email address.';
  else if (email.length > 254 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = 'Enter a valid email address.';
  if (values.attendanceStatus === 'attending' && !values.partySize) errors.partySize = 'Choose the total number attending.';
  if (values.dish.length > 200) errors.dish = 'Keep this to 200 characters or fewer.';
  if (values.dietaryAccessibility.length > 500) errors.dietaryAccessibility = 'Keep this to 500 characters or fewer.';
  if (values.message.length > 1000) errors.message = 'Keep this to 1,000 characters or fewer.';
  return errors;
}

function RSVPForm() {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState('');
  const started = useRef(false);
  const navigate = useNavigate();

  const setValue = (field, value) => {
    if (!started.current) { started.current = true; trackEvent('rsvp_form_started'); }
    const next = { ...values, [field]: value };
    if (field === 'attendanceStatus' && value === 'declined') { next.partySize = ''; next.dish = ''; }
    setValues(next);
    if (touched[field]) setErrors(validateRsvp(next));
  };

  const markTouched = field => {
    setTouched(previous => ({ ...previous, [field]: true }));
    setErrors(validateRsvp(values));
  };

  const errorProps = field => ({
    'aria-invalid': Boolean(errors[field]),
    'aria-describedby': errors[field] ? `${field}-error` : undefined,
  });

  const handleSubmit = async submitEvent => {
    submitEvent.preventDefault();
    setStatus('');
    const validationErrors = validateRsvp(values);
    setErrors(validationErrors);
    setTouched(Object.fromEntries(fieldOrder.map(field => [field, true])));
    if (Object.keys(validationErrors).length) {
      const count = Object.keys(validationErrors).length;
      setStatus(`Please correct ${count} field${count === 1 ? '' : 's'}.`);
      const firstInvalid = fieldOrder.find(field => validationErrors[field]);
      document.querySelector(`[name="${firstInvalid}"]`)?.focus();
      return;
    }
    if (values.website) return;
    setSubmitting(true);
    setStatus('Sending your RSVP…');
    try {
      await submitRsvp(values);
      trackEvent('rsvp_submitted');
      const confirmation = { name: values.name.trim(), attendanceStatus: values.attendanceStatus };
      sessionStorage.setItem('bbabdt-confirmation', JSON.stringify(confirmation));
      navigate('/thankyou', { state: confirmation });
    } catch (_error) {
      trackEvent('rsvp_submission_failed');
      setStatus('We couldn’t send your RSVP. Your information is still here—please try again.');
      setSubmitting(false);
    }
  };

  const attending = values.attendanceStatus === 'attending';
  return (
    <section className="rsvp section" id="rsvp" aria-labelledby="rsvp-title">
      <div className="rsvp__intro">
        <p className="eyebrow">Save your seat</p>
        <h2 id="rsvp-title">Are you in?</h2>
        <p>RSVP by <strong>{event.rsvpDeadline}</strong>. Every field marked “Required” needs an answer.</p>
        <div className="rsvp__aside"><span aria-hidden="true">✦</span><p>There will be turkey. There will be stories. There should probably be you.</p></div>
      </div>

      <form className="rsvp-form" noValidate onSubmit={handleSubmit}>
        <fieldset className={errors.attendanceStatus ? 'fieldset-error' : ''}>
          <legend>Will you be there? <span className="required">Required</span></legend>
          <div className="radio-cards">
            <label>
                <input type="radio" name="attendanceStatus" value="attending" checked={attending} onChange={e => setValue('attendanceStatus', e.target.value)} onBlur={() => markTouched('attendanceStatus')} aria-describedby={errors.attendanceStatus ? 'attendanceStatus-error' : undefined} />
              <span><strong>Yes!</strong> Save me a seat.</span>
            </label>
            <label>
              <input type="radio" name="attendanceStatus" value="declined" checked={values.attendanceStatus === 'declined'} onChange={e => setValue('attendanceStatus', e.target.value)} onBlur={() => markTouched('attendanceStatus')} />
              <span><strong>Not this year.</strong> I’ll be there in spirit.</span>
            </label>
          </div>
          {errors.attendanceStatus && <p className="field-error" id="attendanceStatus-error">{errors.attendanceStatus}</p>}
        </fieldset>

        <div className="form-row">
          <FormField id="name" label="Name" required error={errors.name}>
            <input id="name" name="name" type="text" autoComplete="name" maxLength="100" value={values.name} onChange={e => setValue('name', e.target.value)} onBlur={() => markTouched('name')} {...errorProps('name')} />
          </FormField>
          <FormField id="email" label="Email" required error={errors.email}>
            <input id="email" name="email" type="email" inputMode="email" autoComplete="email" maxLength="254" value={values.email} onChange={e => setValue('email', e.target.value)} onBlur={() => markTouched('email')} {...errorProps('email')} />
          </FormField>
        </div>

        {attending && <>
          <FormField id="partySize" label="Total party size" required error={errors.partySize}>
            <select id="partySize" name="partySize" value={values.partySize} onChange={e => setValue('partySize', e.target.value)} onBlur={() => markTouched('partySize')} {...errorProps('partySize')}>
              <option value="">Choose 1–5</option>
              {[1, 2, 3, 4, 5].map(number => <option value={number} key={number}>{number}</option>)}
            </select>
          </FormField>
          <FormField id="dish" label="Dish or contribution" hint="Optional · 200 characters max" error={errors.dish}>
            <input id="dish" name="dish" type="text" maxLength="200" value={values.dish} onChange={e => setValue('dish', e.target.value)} onBlur={() => markTouched('dish')} aria-describedby={errors.dish ? 'dish-error' : 'dish-hint'} aria-invalid={Boolean(errors.dish)} />
          </FormField>
        </>}

        <FormField id="dietaryAccessibility" label="Dietary or accessibility notes" hint="Optional · 500 characters max" error={errors.dietaryAccessibility}>
          <textarea id="dietaryAccessibility" name="dietaryAccessibility" rows="3" maxLength="500" value={values.dietaryAccessibility} onChange={e => setValue('dietaryAccessibility', e.target.value)} onBlur={() => markTouched('dietaryAccessibility')} aria-describedby={errors.dietaryAccessibility ? 'dietaryAccessibility-error' : 'dietaryAccessibility-hint'} aria-invalid={Boolean(errors.dietaryAccessibility)} />
        </FormField>
        <FormField id="message" label="Anything else?" hint="Optional · 1,000 characters max" error={errors.message}>
          <textarea id="message" name="message" rows="4" maxLength="1000" value={values.message} onChange={e => setValue('message', e.target.value)} onBlur={() => markTouched('message')} aria-describedby={errors.message ? 'message-error' : 'message-hint'} aria-invalid={Boolean(errors.message)} />
        </FormField>

        <div className="honeypot" aria-hidden="true"><label htmlFor="website">Website</label><input id="website" name="website" tabIndex="-1" autoComplete="off" value={values.website} onChange={e => setValue('website', e.target.value)} /></div>
        <button className="button button--submit" type="submit" disabled={submitting}>
          {submitting ? 'Sending…' : attending ? 'Complete RSVP' : 'Send response'} {!submitting && <span aria-hidden="true">→</span>}
        </button>
        <p className={`form-status${status.includes('couldn’t') ? ' form-status--error' : ''}`} role="status" aria-live="polite">{status}</p>
      </form>
    </section>
  );
}

export default RSVPForm;
